/**
 * ExerciseEditModal — universal teacher-only inline editor for any exercise
 * (listening, reading, vocabulary, conjugation…). Lets the teacher fix typos,
 * swap a wrong correct answer or add a missing option without deleting and
 * re-uploading the whole exercise.
 *
 * Calls PATCH /api/teacher/exercises/{id} which accepts title, question,
 * options[], correct_answer, levels[], explanation, audio_text.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Pencil, Plus, X, FloppyDisk, Check, Spinner } from '@phosphor-icons/react';
import { isDraftDate, toLocalInput, scheduleForSave, DRAFT_HINT, scheduleFieldLabel } from '../lib/scheduleDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

export default function ExerciseEditModal({ exercise, open, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState([]);
  const [correctIdx, setCorrectIdx] = useState(0);
  const [levels, setLevels] = useState([]);
  const [explanation, setExplanation] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!exercise) return;
    setTitle(exercise.title || '');
    setQuestion(exercise.question || '');
    const opts = exercise.options || [];
    setOptions(opts.length ? [...opts] : ['', '']);
    // correct_answer can be either a string ("Madrid") or an index (2)
    const ca = exercise.correct_answer;
    if (typeof ca === 'number' && ca >= 0 && ca < opts.length) {
      setCorrectIdx(ca);
    } else {
      const idx = opts.findIndex(o => o === ca);
      setCorrectIdx(idx >= 0 ? idx : 0);
    }
    setLevels(exercise.levels?.length ? [...exercise.levels] : (exercise.level ? [exercise.level] : ['A2']));
    setExplanation(exercise.explanation || '');
    setScheduledAt(toLocalInput(exercise.scheduled_at));
  }, [exercise]);

  if (!open || !exercise) return null;

  const setOpt = (i, v) => setOptions(prev => prev.map((o, idx) => idx === i ? v : o));
  const addOpt = () => options.length < 6 && setOptions(prev => [...prev, '']);
  const rmOpt = (i) => {
    if (options.length <= 2) return;
    setOptions(prev => prev.filter((_, idx) => idx !== i));
    if (correctIdx >= i && correctIdx > 0) setCorrectIdx(c => c - 1);
  };
  const toggleLvl = (l) => setLevels(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l]);

  const save = async () => {
    const cleanOpts = options.map(o => o.trim()).filter(Boolean);
    if (!question.trim()) return toast.error('Frage fehlt');
    if (cleanOpts.length < 2) return toast.error('Mind. 2 Antwortoptionen');
    if (correctIdx >= cleanOpts.length) return toast.error('Wähle die richtige Antwort');
    if (levels.length === 0) return toast.error('Mind. 1 Niveau');
    setBusy(true);
    try {
      const body = {
        title: title.trim() || exercise.title,
        question: question.trim(),
        options: cleanOpts,
        correct_answer: cleanOpts[correctIdx],
        levels,
        level: levels[0],
        explanation: explanation.trim(),
        scheduled_at: scheduleForSave(scheduledAt, exercise.scheduled_at),
        notify: false,
      };
      const res = await axios.patch(`${API}/teacher/exercises/${exercise.exercise_id}`, body, { withCredentials: true });
      toast.success('Übung aktualisiert');
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="exercise-edit-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-[#292524] flex items-center gap-2">
            <Pencil size={18} weight="duotone" className="text-[#AB1C24]" />
            Übung bearbeiten
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="exercise-edit-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Titel (optional)</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="edit-title" />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Frage *</label>
          <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={2} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="edit-question" />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Antwortoptionen — wähle die richtige *</label>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCorrectIdx(i)}
                className={`w-7 h-7 rounded-full flex items-center justify-center border-2 ${correctIdx === i ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-stone-300 text-stone-400 hover:border-emerald-400'}`}
                title="Als richtige Antwort markieren"
                data-testid={`edit-opt-correct-${i}`}
              >
                {correctIdx === i ? <Check size={12} weight="bold" /> : <span className="text-xs font-bold">{i + 1}</span>}
              </button>
              <input value={o} onChange={e => setOpt(i, e.target.value)} className="flex-1 bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid={`edit-opt-${i}`} />
              {options.length > 2 && (
                <button type="button" onClick={() => rmOpt(i)} className="text-stone-400 hover:text-red-500 p-1" data-testid={`edit-opt-remove-${i}`}>
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
          {options.length < 6 && (
            <button type="button" onClick={addOpt} className="inline-flex items-center gap-1 text-xs text-[#AB1C24] font-semibold hover:underline" data-testid="edit-opt-add">
              <Plus size={12} weight="bold" /> Option hinzufügen
            </button>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Niveaus *</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_LEVELS.map(l => (
              <button key={l} type="button" onClick={() => toggleLvl(l)}
                className={`px-3 py-1 rounded-full border text-xs font-semibold ${levels.includes(l) ? 'bg-[#AB1C24] border-[#AB1C24] text-white' : 'bg-white border-stone-300 text-[#57534E] hover:border-[#AB1C24]'}`}
                data-testid={`edit-level-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Erklärung (optional)</label>
          <textarea value={explanation} onChange={e => setExplanation(e.target.value)} rows={2} placeholder="Wird nach der Antwort angezeigt" className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="edit-explanation" />
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider block pt-2">{scheduleFieldLabel(exercise.scheduled_at)}</label>
          <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="edit-exercise-scheduled-at" />
          {isDraftDate(exercise.scheduled_at) && <p className="text-[11px] text-[#78716C]">{DRAFT_HINT}</p>}
        </div>

        <div className="flex gap-2 pt-2 border-t border-stone-200">
          <Button onClick={onClose} variant="outline" className="flex-1" data-testid="edit-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white" data-testid="edit-save">
            {busy ? <Spinner size={13} className="animate-spin mr-2" /> : <FloppyDisk size={13} weight="fill" className="mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
